/**
 * DecayNotification — fixed banner at the top of the screen that surfaces
 * blueberry-decay warnings (Papaya gently calling the seeker back) and lost-
 * blueberry notifications. Extracted from App.js during the App.js split pass
 * — it's fully self-contained, the only dependency is EnlightenmentContext.
 */
import { useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";

const DecayNotification = () => {
  const { decayWarning, decayLost, clearDecayWarning, clearDecayLost } = useContext(EnlightenmentContext);

  return (
    <AnimatePresence>
      {decayLost && (
        <motion.div
          initial={{ opacity: 0, y: -60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -60 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-md"
          data-testid="decay-lost-notification"
        >
          <div className="rounded-2xl p-4 flex items-start gap-3 shadow-2xl"
            style={{ backgroundColor: '#2C1810', border: '1px solid #8B4513' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#5C2D0E' }}>
              <AlertTriangle size={20} style={{ color: '#D4A574' }} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold mb-1" style={{ color: '#D4A574', fontFamily: "'Fredoka', sans-serif" }}>
                Blueberry Lost
              </div>
              <div className="text-xs leading-relaxed" style={{ color: '#C4956A', fontFamily: "'Nunito', sans-serif" }}>
                {decayLost}
              </div>
            </div>
            <button onClick={clearDecayLost} className="flex-shrink-0 mt-0.5" data-testid="decay-lost-dismiss">
              <X size={16} style={{ color: '#8B7DA8' }} />
            </button>
          </div>
        </motion.div>
      )}

      {decayWarning && !decayLost && (
        <motion.div
          initial={{ opacity: 0, y: -60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -60 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-md"
          data-testid="decay-warning-notification"
        >
          <div className="rounded-2xl p-4 flex items-start gap-3 shadow-2xl"
            style={{ backgroundColor: '#1A1030', border: '1px solid #4F378B' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bunny-float"
              style={{ backgroundColor: '#2D1F50' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png"
                alt="Papaya" className="w-8 h-8 rounded-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold mb-1" style={{ color: '#D4A574', fontFamily: "'Fredoka', sans-serif" }}>
                Papaya is calling you back
              </div>
              <div className="text-xs leading-relaxed" style={{ color: '#B0A5C0', fontFamily: "'Nunito', sans-serif" }}>
                {decayWarning.message}
              </div>
            </div>
            <button onClick={clearDecayWarning} className="flex-shrink-0 mt-0.5" data-testid="decay-warning-dismiss">
              <X size={16} style={{ color: '#8B7DA8' }} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DecayNotification;
